import { Node } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import isEmpty from "lodash-es/isEmpty";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import { VariableTokenNodeView } from "./VariableTokenNodeView";
import {
  parseDirectiveRaw,
  buildDirectiveRaw,
  isDirectiveRaw,
} from "../directives/core/parseRaw";

const DATA_ATTR = "data-sparx-var";

export interface VariableTokenExtensionOptions {
  variables: Variables[];
}

export const VariableTokenExtension =
  Node.create<VariableTokenExtensionOptions>({
    name: "variableToken",
    group: "inline",
    inline: true,
    atom: true,

    addOptions() {
      return {
        variables: [],
      };
    },

    addStorage() {
      return {
        getVariables: (): Variables[] => this.options.variables,
      };
    },

    addAttributes() {
      return {
        raw: {
          default: "",
          renderHTML: (attrs: { raw?: string }) => ({
            [DATA_ATTR]: attrs.raw ?? "",
          }),
        },
      };
    },
    parseHTML() {
      return [
        {
          tag: `span[${DATA_ATTR}]`,
          getAttrs: (dom) => {
            if (dom instanceof HTMLElement) {
              const raw = dom.getAttribute(DATA_ATTR);
              if (raw != null && !isEmpty(raw)) {
                const value = /^\{\{.*\}\}$/.test(raw) ? raw.slice(2, -2) : raw;
                return { raw: value };
              }
            }
            return false;
          },
        },
      ];
    },
    renderHTML({ HTMLAttributes }) {
      return ["span", HTMLAttributes];
    },
    addNodeView() {
      return ReactNodeViewRenderer(VariableTokenNodeView, { as: "span" });
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey("variableTokenDirectiveBackspace"),
          props: {
            handleKeyDown(view, event) {
              if (event.key !== "Backspace") return false;
              const { state } = view;
              const { selection } = state;
              if (!selection.empty) return false;
              const { $from } = selection;
              const { nodeBefore } = $from;
              if (
                nodeBefore == null ||
                nodeBefore.type.name !== "variableToken"
              ) {
                return false;
              }
              const raw = nodeBefore.attrs.raw as string;
              if (!isDirectiveRaw(raw)) return false;

              const parsed = parseDirectiveRaw(raw);
              if (parsed == null) return false;

              const nodePos = $from.pos - nodeBefore.nodeSize;
              const { tr } = state;

              if (parsed.choices.length > 0) {
                const newChoices = parsed.choices.slice(0, -1);
                const newRaw = buildDirectiveRaw(
                  parsed.directive,
                  parsed.variable,
                  newChoices,
                );
                tr.setNodeMarkup(nodePos, undefined, { raw: newRaw });
              } else if (parsed.variable.length > 0) {
                const newRaw = buildDirectiveRaw(parsed.directive, "", []);
                tr.setNodeMarkup(nodePos, undefined, { raw: newRaw });
              } else {
                tr.delete(nodePos, $from.pos);
              }

              view.dispatch(tr);
              return true;
            },
          },
        }),
      ];
    },
  });
